import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Page title block: mono title, muted sub, right-aligned actions. */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
            {eyebrow}
          </p>
        )}
        <h1 className="truncate text-2xl font-semibold leading-tight tracking-[-0.03em]">{title}</h1>
        {description && <p className="mt-1.5 max-w-[70ch] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

/** Bordered row of label / mono value cells with hairline dividers. */
export function StatStrip({
  items,
}: {
  items: { label: string; value: React.ReactNode; note?: React.ReactNode; href?: string }[];
}) {
  return (
    <div className="grid grid-cols-1 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card sm:grid-flow-col sm:auto-cols-fr sm:divide-x sm:divide-y-0">
      {items.map((it) => (
        <div key={it.label} className="px-[18px] py-3.5">
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
            {it.label}
          </p>
          <p className="mt-1.5 font-mono text-[26px] font-semibold tabular-nums tracking-[-0.03em]">
            {it.value}
          </p>
          {it.note && <p className="mt-0.5 text-xs text-muted-foreground">{it.note}</p>}
        </div>
      ))}
    </div>
  );
}

/** Identifier (client_id, kid, slug, scope) — always mono, always copyable. */
export function CopyId({ value, className }: { value: string; className?: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-[5px] border border-border bg-card px-1.5 py-0.5 font-mono text-[11.5px]',
        className
      )}
    >
      <span className="truncate">{value}</span>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Copied' : `Copy ${value}`}
        className="text-muted-foreground hover:text-foreground"
      >
        {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

/** Status pill with a leading dot. tone maps to success / warning / destructive / neutral. */
export function StatusPill({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'bad';
  children: React.ReactNode;
}) {
  const tones = {
    neutral: 'bg-foreground/[0.06] text-muted-foreground',
    ok: 'bg-success/[0.12] text-success',
    warn: 'bg-warning/[0.14] text-warning',
    bad: 'bg-destructive/[0.12] text-destructive',
  } as const;
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11.5px] font-semibold before:h-1.5 before:w-1.5 before:rounded-full before:bg-current',
        tones[tone]
      )}
    >
      {children}
    </span>
  );
}

/** Segmented filter control (design `.seg`). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; count?: number }[];
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-foreground/[0.03] p-0.5" role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground',
            value === o.value && 'bg-card text-foreground ring-1 ring-border'
          )}
        >
          {o.label}
          {o.count !== undefined && (
            <span className="font-mono text-[11px] text-muted-foreground">{o.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
